/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { Route } from './+types/_app.gift-guide';
import { Link } from '@/components/link';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SeoMeta } from '@/components/seo-meta';
import { collectNavCategoryUrls } from '@/lib/api/category-slug.server';
import { CategoryHrefProvider, useCategoryHref, type CategoryUrlMap } from '@/components/navigation-menu/category-href';

export const handle = {
    ui: {
        main: {
            backgroundClass: 'bg-background',
        },
    },
};

/**
 * Resolve the `bags` category link to its production SEO slug so this page emits the same URL prod
 * (SFRA) serves, rather than the internal `/category/bags` id route (which would otherwise rely on the
 * 301). `collectNavCategoryUrls` is deduped onto the layout's nav fetch on SSR (free), never throws, and
 * degrades to an empty map — then `useCategoryHref` falls back to the id route (the 301 heals it).
 */
export async function loader({ context }: Route.LoaderArgs): Promise<{ categoryUrls: CategoryUrlMap }> {
    return { categoryUrls: await collectNavCategoryUrls(context) };
}

// All gift-guide CTAs point at the same `bags` category; the href is resolved once (to the SEO slug)
// in the content component below and shared across every tile and banner link.
const MOST_LOVED = [
    {
        name: 'Triple Zip Bagg',
        description: 'Three zipped compartments keep everything in its place — a go-everywhere essential.',
        img: '/images/gift-guide/sub1-img1.jpg',
    },
    {
        name: 'Park Ave Weekender',
        description: "Carry-on approved, impeccably organized. The weekend bag she'll reach for every time.",
        img: '/images/gift-guide/sub1-img2.jpg',
    },
    {
        name: 'Securtex Anti-Theft Belt Bag Sling',
        description: 'Slash-resistant construction, RFID blocking, and hands-free ease all in one.',
        img: '/images/gift-guide/sub1-img3.jpg',
    },
];

const PRICE_TIERS = [
    {
        label: 'Gifts $50 & Under',
        description: 'Thoughtful picks that feel like a splurge — no matter the budget.',
        img: '/images/gift-guide/sub2.jpg',
    },
    {
        label: 'Gifts $100 & Under',
        description: "The sweet spot: bags she'll carry for years, priced to please.",
        img: '/images/gift-guide/sub3.jpg',
    },
    {
        label: 'Gifts $200 & Under',
        description: 'Premium styles for the person who deserves something special.',
        img: '/images/gift-guide/sub4.jpg',
    },
];

export default function GiftGuide({ loaderData }: Route.ComponentProps): ReactElement {
    return (
        <CategoryHrefProvider value={loaderData.categoryUrls}>
            <GiftGuideContent />
        </CategoryHrefProvider>
    );
}

function GiftGuideContent(): ReactElement {
    const bagsHref = useCategoryHref()('bags');
    return (
        <div className="pb-16 bg-background">
            <SeoMeta
                title="Gift Guide — Baggallini"
                description="Shop the Baggallini gift guide — curated picks at every price point. Find the perfect bag for every woman on your list."
            />

            {/* Breadcrumb */}
            <div className="max-w-screen-2xl mx-auto px-4 pt-4 pb-2">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to="/">Home</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Gift Guide</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>

            {/* Hero */}
            <div className="relative overflow-hidden">
                <img
                    src="/images/gift-guide/hero.jpg"
                    alt="Baggallini Gift Guide — gifts you want to give and get"
                    className="w-full object-cover object-center"
                    style={{ maxHeight: '640px' }}
                />
                <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-center px-6">
                    <p className="text-primary-foreground text-xs tracking-[0.18em] uppercase font-medium mb-3">
                        Holiday 2025
                    </p>
                    <h1
                        style={{ fontFamily: 'var(--font-serif)' }}
                        className="text-primary-foreground text-5xl sm:text-6xl lg:text-7xl font-light leading-tight mb-4 max-w-2xl">
                        The gifts you want to give and get
                    </h1>
                    <p className="text-primary-foreground/85 text-base sm:text-lg max-w-xl mb-8 leading-relaxed">
                        Shop gifts that will elevate her daily style and function with hands-free ease.
                    </p>
                    <Link
                        to={bagsHref}
                        className="inline-block bg-background text-[#1a1a1a] text-xs tracking-[0.12em] uppercase font-medium px-8 py-3 hover:bg-background/90 transition-colors">
                        Shop Now
                    </Link>
                </div>
            </div>

            {/* Most Loved */}
            <div className="max-w-screen-xl mx-auto px-6 py-16 sm:py-20">
                <div className="text-center mb-12">
                    <p className="text-xs tracking-[0.18em] uppercase text-[#888] font-medium mb-2">Our Picks</p>
                    <h2
                        style={{ fontFamily: 'var(--font-serif)' }}
                        className="text-4xl sm:text-5xl font-light text-[#1a1a1a]">
                        Most Loved
                    </h2>
                    <p className="mt-4 text-[#555] max-w-lg mx-auto leading-relaxed">
                        Thoughtful, functional gifts she&apos;ll reach for every single day.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                    {MOST_LOVED.map((item) => (
                        <Link key={item.name} to={bagsHref} className="group block">
                            <div className="overflow-hidden aspect-[3/4] mb-4">
                                <img
                                    src={item.img}
                                    alt={item.name}
                                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                                />
                            </div>
                            <h3
                                style={{ fontFamily: 'var(--font-serif)' }}
                                className="text-xl font-light text-[#1a1a1a] mb-1">
                                {item.name}
                            </h3>
                            <p className="text-sm text-[#666] leading-relaxed mb-3">{item.description}</p>
                            <span className="text-xs tracking-[0.12em] uppercase font-medium text-[#1a1a1a] underline underline-offset-4">
                                Shop Now
                            </span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Price Tier Sections */}
            <div className="bg-[#F5F3F0] py-16 sm:py-20">
                <div className="max-w-screen-xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <p className="text-xs tracking-[0.18em] uppercase text-[#888] font-medium mb-2">
                            Shop By Budget
                        </p>
                        <h2
                            style={{ fontFamily: 'var(--font-serif)' }}
                            className="text-4xl sm:text-5xl font-light text-[#1a1a1a]">
                            Gifts for Every Budget
                        </h2>
                        <p className="mt-4 text-[#555] max-w-lg mx-auto leading-relaxed">
                            From stocking stuffers to statement pieces — find the perfect gift at the perfect price.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {PRICE_TIERS.map((tier) => (
                            <Link key={tier.label} to={bagsHref} className="group block bg-background overflow-hidden">
                                <div className="overflow-hidden aspect-[4/3]">
                                    <img
                                        src={tier.img}
                                        alt={tier.label}
                                        className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-6">
                                    <h3
                                        style={{ fontFamily: 'var(--font-serif)' }}
                                        className="text-2xl font-light text-[#1a1a1a] mb-2">
                                        {tier.label}
                                    </h3>
                                    <p className="text-sm text-[#666] leading-relaxed mb-4">{tier.description}</p>
                                    <span className="inline-block bg-[#1a1a1a] text-primary-foreground text-xs tracking-[0.12em] uppercase font-medium px-6 py-3 group-hover:bg-[#333] transition-colors">
                                        Shop Gifts
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {/* New Holiday Collection */}
            <div className="relative overflow-hidden">
                <img
                    src="/images/gift-guide/sub5.jpg"
                    alt="New Holiday Collection"
                    className="w-full object-cover object-center"
                    style={{ maxHeight: '560px' }}
                />
                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center text-center px-6">
                    <p className="text-primary-foreground text-xs tracking-[0.18em] uppercase font-medium mb-3">
                        Arriving Now
                    </p>
                    <h2
                        style={{ fontFamily: 'var(--font-serif)' }}
                        className="text-primary-foreground text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mb-4 max-w-xl">
                        New Holiday Collection
                    </h2>
                    <p className="text-primary-foreground/85 text-base max-w-md mb-8 leading-relaxed">
                        Fresh styles made for the season. Discover limited-edition colors and designs crafted for
                        gifting.
                    </p>
                    <Link
                        to={bagsHref}
                        className="inline-block bg-background text-[#1a1a1a] text-xs tracking-[0.12em] uppercase font-medium px-8 py-3 hover:bg-background/90 transition-colors">
                        Explore the Collection
                    </Link>
                </div>
            </div>

            {/* Email CTA Strip */}
            <div className="bg-[#1a1a1a] py-14 text-center px-6">
                <h2
                    style={{ fontFamily: 'var(--font-serif)' }}
                    className="text-primary-foreground text-4xl font-light mb-3">
                    Get 15% Off Your First Purchase
                </h2>
                <p className="text-primary-foreground/70 max-w-md mx-auto mb-7 text-sm leading-relaxed">
                    Sign up for emails and be the first to know about new arrivals, exclusive offers, and gift
                    inspiration.
                </p>
                <Link
                    to={bagsHref}
                    className="inline-block bg-background text-[#1a1a1a] text-xs tracking-[0.12em] uppercase font-medium px-8 py-3 hover:bg-background/90 transition-colors">
                    Shop All Gifts
                </Link>
            </div>
        </div>
    );
}
